import React, { useState, useEffect, ChangeEvent } from 'react';

import { colaboradorService } from '@core/services';
import { selectorHolerite } from '@core/slice/holerite.slice';
import { formatCurrency, formatDate, getDateFromSap, isFilled, isObjectEmpty } from '@core/utils';
import { useAppSelector } from '@store/hooks';
import { Button, Container, Divider, Grid, Skeleton, Typography, FormControlLabel, Radio } from '@mui/material';
import { AttachMoney, Visibility, VisibilityOff } from '@mui/icons-material';
import { DatePicker, LoadingView } from '@core/components';
import dayjs, { Dayjs } from 'dayjs';
import { AlertComponent } from '@core/components/atoms/alert-component';
import { ProventosDescontos } from './components';

interface IHoleriteProps {}

export const Holerite: React.FC<IHoleriteProps> = () => {
    const paySlipTypes = {
        salarioMensal: {
            id: 11,
            name: 'Mensal'
        },
        adiantamento13Salario: {
            id: 31,
            name: '13º Adiantamento'
        },
        integral13Salario: {
            id: 32,
            name: '13º Integral'
        }
    };

    const { error, loading, data } = useAppSelector(selectorHolerite);
    const [showValues, setShowValues] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
        dayjs()
            .subtract(dayjs().date() > 5 ? 1 : 2, 'month')
            .startOf('month')
    );
    const [selectedPaySlipType, setSelectedPaySlipType] = useState(paySlipTypes.salarioMensal.id);

    useEffect(() => {
        colaboradorService.buscarHolerite(selectedPaySlipType, dayjs(selectedDate).format('YYYY-MM-DD'));
    }, []);

    const handleDateChange = (newDate: Dayjs | null) => {
        setSelectedDate(newDate);
        setSelectedPaySlipType(paySlipTypes.salarioMensal.id);
        colaboradorService.buscarHolerite(paySlipTypes.salarioMensal.id, dayjs(newDate).format('YYYY-MM-DD'));
    };

    const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSelectedPaySlipType(parseInt(e.target.value));
        colaboradorService.buscarHolerite(e.target.value, dayjs(selectedDate).format('YYYY-MM-DD'));
    };

    const paySlipTypeRender = () => {
        return (
            <Grid sx={{ marginTop: '1rem' }}>
                <Grid sx={{ display: 'flex' }} flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'start', md: 'center' }}>
                    {Object.entries(paySlipTypes).map(([key, item]) => (
                        <FormControlLabel
                            key={key}
                            control={<Radio value={item.id} checked={selectedPaySlipType === item.id} onChange={handleRadioChange} sx={{ paddingTop: '4px', paddingBottom: '4px' }} />}
                            label={item.name}
                        />
                    ))}
                </Grid>
            </Grid>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ px: '2rem' }}>
            <Grid>
                <DatePicker selectedDate={selectedDate} onDateChange={handleDateChange} />
            </Grid>

            {loading && <LoadingView />}

            {isFilled(error) && !loading ? (
                <>
                    {paySlipTypeRender()}
                    <AlertComponent type="error" detailed={true} message={error ?? '-'} />
                </>
            ) : isObjectEmpty(data) && !loading ? (
                <AlertComponent type="info" detailed={true} message="Pesquise o demonstrativo." />
            ) : !loading ? (
                <>
                    {paySlipTypeRender()}
                    <Grid sx={{ marginTop: '1rem' }}>
                        <Typography>Valor liquido</Typography>
                        <Grid sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            {showValues ? (
                                <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }} data-clarity-mask="true">
                                    {formatCurrency(Number(data.valorLiquido ?? 0))}
                                </Typography>
                            ) : (
                                <Skeleton variant="rounded" animation={false} width="6rem" height="1rem" />
                            )}
                            <Button onClick={() => setShowValues(!showValues)}>{showValues ? <Visibility sx={{ cursor: 'pointer' }} /> : <VisibilityOff sx={{ cursor: 'pointer' }} />}</Button>
                        </Grid>
                    </Grid>
                    <Grid sx={{ marginTop: '1rem' }}>
                        <Typography sx={{ fontWeight: 'bold' }}>Pago em {formatDate(getDateFromSap(data.dataPagamento), false)}</Typography>

                        {/* Proventos */}
                        <Grid sx={{ marginTop: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center'
                                }}
                            >
                                <AttachMoney style={{ color: 'green' }} />
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>Proventos</Typography>
                            </Grid>
                            {showValues ? (
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }} data-clarity-mask="true">
                                    {formatCurrency(data.valorProventos ?? 0)}
                                </Typography>
                            ) : (
                                <Skeleton variant="rounded" animation={false} width="6rem" height="1rem" />
                            )}
                        </Grid>
                        <Divider sx={{ mt: '0.5rem', mb: '1rem' }} />
                        <Grid sx={{ marginLeft: '1.6rem', display: 'flex', flexDirection: 'column' }}>
                            {data.proventos && !loading ? (
                                data.proventos.map((provento, index) => <ProventosDescontos key={index} data={provento} showValues={showValues} />)
                            ) : !loading ? (
                                <AlertComponent type="info" detailed={true} message="Lista de proventos indisponível." />
                            ) : (
                                ''
                            )}
                        </Grid>

                        {/* Descontos */}
                        <Grid sx={{ marginTop: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center'
                                }}
                            >
                                <AttachMoney style={{ color: 'red' }} />
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>Descontos</Typography>
                            </Grid>
                            {showValues ? (
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }} data-clarity-mask="true">
                                    {formatCurrency(data.valorDescontos ?? 0)}
                                </Typography>
                            ) : (
                                <Skeleton variant="rounded" animation={false} width="6rem" height="1rem" />
                            )}
                        </Grid>
                        <Divider sx={{ mt: '0.5rem', mb: '1rem' }} />
                        <Grid sx={{ marginLeft: '1.6rem', display: 'flex', flexDirection: 'column' }}>
                            {data.descontos && !loading ? (
                                data.descontos.map((desconto, index) => <ProventosDescontos key={index} data={desconto} showValues={showValues} />)
                            ) : !loading ? (
                                <AlertComponent type="info" detailed={true} message="Lista de descontos indisponível." />
                            ) : (
                                ''
                            )}
                        </Grid>
                    </Grid>
                </>
            ) : (
                ''
            )}
        </Container>
    );
};
